import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Card,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import MoreMenu from "./MoreMenu.js";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import { applySortFilter } from "../../utils/tableFunctions";
import { APP_USER_SESSION } from "../../constant/AppConstants.js";
import { StorageUtils } from "../../utils/index.js";
import {
  getPayoutInfo,
  payoutAction,
  getPayoutFilterConditions,
  Log,
} from "../../services/services.js";
import EditPayoutDialog from "./Dialogs/EditPayoutDialog.js";
import { StyledTableRow } from "../TableRow.js";
import FormSearch from "../FormSearch.js";
import CircularLoader from "../circularLoader.js";
import Label from "../Label.js";

const defaultValue = {
  is_active: true
}

const TABLE_HEAD = [
  { id: "merchant", label: "Merchant Name" },
  { id: "payment_host", label: "Payment Host" },
  { id: "is_active", label: "Active" },
  { id: "menu" },
];

const CustomTableCell = ({ align, field, id }) => {
  return (
    <TableCell
      align={align}
      onClick={() => {
        window.open(`/3/app/details/payout/${id}`, "_blank");
      }}
    >
      {field}
    </TableCell>
  );
};

function Payout() {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [loaded, setLoaded] = useState(false);

  const [payout, setPayout] = useState([]);

  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION).role;

  //dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredList = applySortFilter(
    payout || [],
    order,
    orderBy
  );

  const isItemNotFound = filteredList.length === 0;

  const DeletePayoutDialog = ({ open, selectedId, setResponse, setLoaded }) => {

    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: yup.object().shape({
        reason: yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        Log.info(`Click button [Submit] - Delete Merchant Payout Info by Id: ${selectedId}`, values);
        await payoutAction("REMOVE", setResponse, setLoaded, { action_message: values.reason }, selectedId);
        handleClose();
      }
    });
  
    const handleClose = () => {
      setOpenDeleteDialog(false);
      resetForm();
    };


    return (
      <Dialog open={open} fullWidth>
        <Stack
          direction="row"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pr: 2,
            alignItems: "center",
            bgcolor: "primary.main",
          }}
        >
          <DialogTitle sx={{ color: "#fff" }}>
            Delete this Payout Info?
          </DialogTitle>
          <Stack direction="row" spacing={2} sx={{ height: 35 }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button variant="contained" color="success" onClick={handleSubmit}>
              Submit
            </Button>
          </Stack>
        </Stack>
        <DialogContent dividers>
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item md={5} xs={5}>
              <Typography variant="subtitle1">Reason *</Typography>
            </Grid>
            <Grid item md={7} xs={7}>
              <TextField
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Please enter your reason"
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
              />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    );
  };

  const renderData = data => {
    const newData = data.map(item => ({
      ...item,
      merchant: item.merchant?.name,
      payment_host: item.payment_host?.name
    }))
    setPayout(newData);
  }

  useEffect(() => {
    getPayoutInfo(renderData, setLoaded, defaultValue);
    getPayoutFilterConditions(setPayoutFilter, setFLoaded);
  }, []);

  // filter
  const [payoutFilter, setPayoutFilter] = useState("");
  const [fLoaded, setFLoaded] = useState(false);
  
  const onFilterSubmit = (fParams) => {
    Log.info('Click button [Apply Filter] - Search Merchant Payout list', fParams);
    setLoaded(false);
    getPayoutInfo(renderData, setLoaded, fParams);
  };

  const schema = [
    {
      type: "select",
      label: "Merchant",
      param: "merchant",
      options: payoutFilter?.merchant?.map((merchant) => ({
        name: merchant.merchant__name,
        id: merchant.merchant__id,
      }))
    },
    {
      type: "select",
      label: "Payment Host",
      param: "payment_host",
      options: payoutFilter?.payment_host?.map((payment_host) => ({
        name: payment_host.payment_host__name,
        id: payment_host.payment_host__id,
      }))
    },
    {
      type: "select",
      label: "Active",
      param: "is_active",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    },
  ]

  return (
    <div>
      <Box>
        <FormSearch
          schema={schema}
          loading={!fLoaded}
          onSearch={onFilterSubmit}
          defaultValue={defaultValue}
        />
        {loaded ?
          <Card>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <ListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    onRequestSort={handleRequestSort}
                    checkbox={false}
                  />
                  <TableBody>
                    {filteredList.map(({ id, merchant, payment_host, is_active }) => {
                        return (
                          <StyledTableRow
                            hover
                            key={id}
                            sx={{ cursor: "pointer" }}
                          >
                            <CustomTableCell
                              field={merchant}
                              id={id}
                            />
                            <CustomTableCell
                              field={payment_host}
                              id={id}
                            />
                            <CustomTableCell
                              field={
                                <Label color={is_active ? "success" : "error"}>
                                  {is_active ? "YES" : "NO"}
                                </Label>
                              }
                              id={id}
                            />
                            <TableCell align="right">
                              <MoreMenu
                                setOpenDeleteDialog={setOpenDeleteDialog}
                                setOpenEditDialog={setOpenEditDialog}
                                setRow={() => setSelectedId(id)}
                                userRole={userRole}
                              />
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                  </TableBody>
                  {isItemNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
          :
          <CircularLoader />
        }
        {selectedId && (
          <>
            <DeletePayoutDialog
              open={openDeleteDialog}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
            />
            <EditPayoutDialog
              open={openEditDialog}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
            />
          </>
        )}
      </Box>
    </div>
  );
}

export default Payout;
